import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getAllAvailablePrompts, reloadCache as reloadCacheApi, type PromptResponse } from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

export interface CategoryItem {
  name: string;
  prompts: PromptResponse[];
}

export interface MetaCategoryItem {
  name: string;
  categories: CategoryItem[];
}

export const usePromptLibraryStore = defineStore('promptLibrary', () => {
  // State
  const availablePrompts = ref<string[]>([]);
  // Values are `| undefined` because collections are filled lazily by name —
  // an arbitrary key may be absent at runtime.
  const promptCollections = ref<Record<string, PromptResponse[] | undefined>>({});
  const allPrompts = ref<PromptResponse[]>([]);
  const isLoading = ref<boolean>(false);
  const loadingCollections = ref<Set<string>>(new Set()); // Track which collections are being loaded
  const error = ref<string | null>(null);
  const initialized = ref<boolean>(false);
  const isInitializing = ref<boolean>(false); // Flag to prevent multiple simultaneous calls
  const isReloadingCache = ref<boolean>(false);

  // Getters
  const metaCategories = computed<MetaCategoryItem[]>(() => {
    const result: MetaCategoryItem[] = [];
    const metaCategoryMap = new Map<string, MetaCategoryItem>();

    // Group prompts by tags (categories)
    for (const prompt of allPrompts.value) {
      if (prompt.tags.length === 0) continue;

      // Use the first tag as the meta category
      const tags = Array.isArray(prompt.tags) ? prompt.tags : [prompt.tags];
      const tag = tags[0];

      if (!tag) continue;

      // Split the tag into meta category and category
      const splitIndex = tag.indexOf('-');
      if (splitIndex === -1) continue;

      const metaCategoryName = tag.substring(0, splitIndex).trim();
      const categoryName = tag.substring(splitIndex + 1).trim();

      // Get or create meta category
      if (!metaCategoryMap.has(metaCategoryName)) {
        metaCategoryMap.set(metaCategoryName, {
          name: metaCategoryName,
          categories: [],
        });
      }

      const metaCategory = metaCategoryMap.get(metaCategoryName);

      // Find or create category
      if (metaCategory) {
        let category = metaCategory.categories.find(c => c.name === categoryName);

        if (!category) {
          category = {
            name: categoryName,
            prompts: [],
          };
          metaCategory.categories.push(category);
        }

        // Add prompt to category
        category.prompts.push(prompt);
      }
    }

    // Convert map to array
    for (const value of metaCategoryMap.values()) {
      result.push(value);
    }

    return result;
  });

  const isCollectionLoading = (name: string): boolean => {
    return loadingCollections.value.has(name);
  };

  // Actions
  async function initialize(): Promise<void> {
    // Prevent multiple simultaneous calls
    if (isInitializing.value || initialized.value) {
      return;
    }

    isInitializing.value = true;
    initialized.value = false;
    isLoading.value = true;
    error.value = null;

    // Reset all state variables when initializing
    availablePrompts.value = [];
    promptCollections.value = {};
    allPrompts.value = [];
    loadingCollections.value.clear();

    try {
      // Fetch all prompts at once using the new endpoint
      const response = await getAllAvailablePrompts();

      if (!response.data) {
        throw new Error('Failed to fetch all available prompts');
      }
      const prompts = response.data;
      allPrompts.value = prompts;

      // Extract available prompt collections from tags
      const collections = new Set<string>();
      for (const prompt of prompts) {
        for (const tag of prompt.tags) collections.add(tag);
      }

      availablePrompts.value = Array.from(collections);

      // Group prompts by collection
      for (const collection of availablePrompts.value) {
        promptCollections.value[collection] = prompts.filter(prompt => {
          const tags = Array.isArray(prompt.tags) ? prompt.tags : [prompt.tags];
          return tags.includes(collection);
        });
      }

      initialized.value = true;
    } catch (error_) {
      error.value =
        error_ instanceof Error ? error_.message : 'Failed to initialize prompt library';
    } finally {
      isLoading.value = false;
      isInitializing.value = false; // Reset the initializing flag
    }
  }

  // For backward compatibility
  async function getPromptCollection(name: string): Promise<PromptResponse[]> {
    // Return cached data if available
    const cached = promptCollections.value[name];
    if (cached && cached.length > 0) {
      return cached;
    }

    // If we've already loaded all prompts, filter by collection name
    if (initialized.value && allPrompts.value.length > 0) {
      const filtered = allPrompts.value.filter(prompt => {
        const tags = Array.isArray(prompt.tags) ? prompt.tags : [prompt.tags];
        return tags.includes(name);
      });

      promptCollections.value[name] = filtered;
      return filtered;
    }

    // If not initialized, initialize first
    if (!initialized.value) {
      // Check if initialization is already in progress
      if (isInitializing.value) {
        // Wait for initialization to complete
        await new Promise<void>(resolve => {
          const checkInterval = setInterval(() => {
            if (!isInitializing.value) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });
      } else {
        await initialize();
      }
      return promptCollections.value[name] ?? [];
    }

    return [];
  }

  /**
   * Reloads the prompt cache from the server and reinitializes the store
   * @returns Promise<void>
   */
  async function reloadCache(): Promise<void> {
    try {
      isReloadingCache.value = true;
      error.value = null;

      await reloadCacheApi();

      // Reinitialize to fetch the new data
      await initialize();
    } catch (error_) {
      error.value = extractErrorDetail(error_, 'Failed to reload prompt cache');
      throw new Error(error.value);
    } finally {
      isReloadingCache.value = false;
    }
  }

  return {
    availablePrompts,
    promptCollections,
    allPrompts,
    isLoading,
    error,
    initialized,
    metaCategories,
    isCollectionLoading,
    initialize,
    getPromptCollection,
    isInitializing,
    reloadCache,
    isReloadingCache,
  };
});
